import React, { useEffect, useRef, useState } from "react";

// ─── Mobile swipe carousel + desktop mosaic grid ──────────────────────────────
// Mobile: horizontal snap-scroll carousel with a "1 / N" counter that tracks
// scroll position. Desktop (≥900px): classic 1-large + up-to-4-small mosaic.
// Both markups are always in the DOM; CSS media queries pick which is visible,
// so there's no resize listener needed.

const PhotoGallery: React.FC<{ images: string[]; name: string; onOpen: (index: number) => void }> = ({
  images, name, onOpen,
}) => {
  const count = images.length;
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        setActiveIdx(Math.max(0, Math.min(count - 1, idx)));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [count]);

  if (count === 0) return null;

  const sideImages = images.slice(1, 5);
  const gridModifier = sideImages.length >= 4 ? "" : sideImages.length >= 2 ? " pab-gal-grid--four" : sideImages.length === 1 ? " pab-gal-grid--two" : " pab-gal-grid--one";

  return (
    <div className="pab-gallery">
      {/* Mobile swipe carousel */}
      <div className="pab-gal-carousel">
        <div className="pab-gal-carousel__track" ref={trackRef}>
          {images.map((url, i) => (
            <button
              type="button"
              key={url + i}
              className="pab-gal-carousel__slide"
              onClick={() => onOpen(i)}
              aria-label={`${name} — ${i + 1}`}
            >
              <img src={url} alt={`${name} — ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} />
            </button>
          ))}
        </div>
        {count > 1 && (
          <span className="pab-gal-carousel__counter">{activeIdx + 1} / {count}</span>
        )}
      </div>

      {/* Desktop mosaic */}
      {count === 1 ? (
        <div className="pab-gal-grid pab-gal-grid--single">
          <button type="button" className="pab-gal-cell pab-gal-main" onClick={() => onOpen(0)}>
            <img src={images[0]} alt={name} />
          </button>
        </div>
      ) : (
        <div className={`pab-gal-grid${gridModifier}`}>
          <button type="button" className="pab-gal-cell pab-gal-main" onClick={() => onOpen(0)}>
            <img src={images[0]} alt={name} />
          </button>
          {sideImages.map((url, i) => (
            <button type="button" key={url + i} className="pab-gal-cell" onClick={() => onOpen(i + 1)}>
              <img src={url} alt={`${name} — ${i + 2}`} />
              {i === sideImages.length - 1 && count > 5 && (
                <span className="pab-gal-more">+{count - 5}</span>
              )}
            </button>
          ))}
        </div>
      )}
      {count > 1 && (
        <button type="button" className="pab-gal-viewall" onClick={() => onOpen(0)}>
          ⊞ Sve fotografije · {count}
        </button>
      )}
    </div>
  );
};

export default PhotoGallery;

// ─── Lightbox ──────────────────────────────────────────────────────────────────

export const Lightbox: React.FC<{ images: string[]; name: string; startAt: number; onClose: () => void }> = ({
  images, name, startAt, onClose,
}) => {
  const [idx, setIdx] = useState(startAt);

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pab-lightbox" onClick={onClose}>
      <div className="pab-lightbox__header" onClick={(e) => e.stopPropagation()}>
        <span className="pab-lightbox__title">{name}</span>
        <span className="pab-lightbox__counter">{idx + 1} / {images.length}</span>
        <button className="pab-lightbox__close" onClick={onClose} aria-label="Zatvori">✕</button>
      </div>

      <div className="pab-lightbox__stage" onClick={(e) => e.stopPropagation()}>
        {images.length > 1 && (
          <button className="pab-lightbox__nav pab-lightbox__nav--prev" onClick={prev} aria-label="Prethodna">‹</button>
        )}
        <img key={idx} src={images[idx]} alt={`${name} — ${idx + 1}`} className="pab-lightbox__img" />
        {images.length > 1 && (
          <button className="pab-lightbox__nav pab-lightbox__nav--next" onClick={next} aria-label="Sljedeća">›</button>
        )}
      </div>

      {images.length > 1 && (
        <div className="pab-lightbox__strip" onClick={(e) => e.stopPropagation()}>
          {images.map((url, i) => (
            <button
              key={url}
              className={`pab-lightbox__thumb${i === idx ? " pab-lightbox__thumb--active" : ""}`}
              onClick={() => setIdx(i)}
            >
              <img src={url} alt={`${i + 1}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
